import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import DynamicRender from "../controls/DynamicRender";
import EnvironmentTemplate from "../../services/environmentTemplate";
import PubSub from "../../services/PubSub";
import Utils from "../../utils/utils";
import Toolbar from "../controls/Toolbar";
import TextBox from "../controls/form/TextBox";
import ConfirmationPrompt from "../controls/ConfirmationPrompt";
import { useHasPermission } from "../../common/Hooks";
import { Button } from "react-bootstrap";
import SettingsModal from "../settings/SettingsModal";

const EnvironmentTemplateDetails = () => {
    const [translate] = useTranslation();
    const { id } = useParams();
    const [environmentTemplate, setEnvironmentTemplate] = useState({});
    const [render, setRender] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "domain"
    });

    const [showSettings, setShowSettings] = useState(false);
    const [settingsSetId, setSettingsSetId] = useState("");

    const getEnvironmentTemplate = useCallback(async (id) => {
        const response = await EnvironmentTemplate.getEnvironmentTemplate(id);
        if (!response.error && response.data) {
            setEnvironmentTemplate(response.data);
            setRender(true);
            setShowSaveButton(false);
            setSettingsSetId(response.data.settingsSet);
        }
    }, [setEnvironmentTemplate, setRender, setShowSaveButton]);

    useEffect(() => {
        if (id) {
            getEnvironmentTemplate(id);
        }
    }, [id, getEnvironmentTemplate]);

    useEffect(() => {
        if (showSaveButton) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [showSaveButton]);

    const updateEnvironmentTemplate = useCallback(async () => {
        const response = await EnvironmentTemplate.updateEnvironmentTemplate(id, {
            name: environmentTemplate.name,
        });
        if (!response.error) {
            setShowSaveButton(false);
        }
    }, [environmentTemplate, id]);

    const deleteEnvironmentTemplate = useCallback(async () => {
        const response = await EnvironmentTemplate.deleteEnvironmentTemplate(id);
        if (!response.error) {
            setRender(false);
            setShowConfirmation(false);
            PubSub.publish("refresh-environment-template-list", { environmentDeleted: true });
        }
    }, [id, setRender, setShowConfirmation]);

    const handleDelete = () => {
        setShowConfirmation(true);
    };

    const renderEnvironmentDetails = useMemo(() => (
        <DynamicRender render={render}>
            <ConfirmationPrompt show={showConfirmation} message={translate("OTM_DELETE_ENVIRONMENT_?")}
                onConfirm={deleteEnvironmentTemplate} onCancel={() => setShowConfirmation(false)} />
            <div className="col-12 mt-3">
                <div className="card h-100">
                    <div className="card-header border-bottom">
                        <Toolbar
                            buttons={[
                                {
                                    name: "Save",
                                    title: translate("OTM_SAVE"),
                                    onClick: updateEnvironmentTemplate,
                                    hidden: !showSaveButton
                                },
                                {
                                    name: "DeleteOutlined",
                                    title: translate("OTM_DELETE"),
                                    onClick: handleDelete,
                                    disabled: !permissions.delete
                                }
                            ]}
                            heading={translate("OTM_ENVIRONMENT_TEMPLATE_DETAILS")}
                        />
                    </div>
                    <div className="card-body">
                        <Formik initialValues={{
                            name: environmentTemplate.name
                        }} validationSchema={Yup.object({
                            name: Yup.string().min(2, translate("OTM_CHARACTERS_OR_MORE", { minCharacters: 2 })).required(" ")
                        })} enableReinitialize>
                            <Form onChange={(event) => {
                                setEnvironmentTemplate({ ...environmentTemplate, [event.target.name]: event.target.value });
                                if (event.target.value.length >= 2) {
                                    setShowSaveButton(true);
                                } else {
                                    setShowSaveButton(false);
                                }
                            }}>
                                <div className="row mt-n3">
                                    <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_NAME")} type="text" name="name" readOnly={!permissions.update} />
                                    <div className="col-12 mt-3">
                                        <Button
                                            disabled={!permissions.read}
                                            variant="primary"
                                            onClick={() => setShowSettings(true)}>
                                            {permissions.update ? translate("OTM_EDIT_SETTINGS") : translate("OTM_VIEW_SETTINGS")}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        </Formik>
                    </div>
                </div>
            </div>
            {
                settingsSetId &&
                <SettingsModal
                    show={showSettings}
                    onCancel={() => setShowSettings(false)}
                    settingsSetId={settingsSetId}
                    permissions={permissions}
                    context={{ type: 'environmentTemplate', headerText: environmentTemplate.name, objectId: Utils.getDomain() }} />
            }
        </DynamicRender>
    ), [deleteEnvironmentTemplate, settingsSetId, showSettings, environmentTemplate, permissions,
        render, showConfirmation, showSaveButton, translate, updateEnvironmentTemplate]);

    return (
        <>
            {renderEnvironmentDetails}
        </>
    );
};

export default EnvironmentTemplateDetails;
